import { BookingSlot, Patient } from "@cur8/rich-entity";
import { useNav, useQueryParams } from "@pomle/react-router-paths";
import { ViewStack } from "@pomle/react-viewstack";
import { Direction, Slide } from "@pomle/react-viewstack-transitions";
import { LegalDocument, generateDocumentUrl, hasAgreedTo } from "lib/legal";
import { isOnboardingComplete } from "lib/onboarding/isOnboardingComplete";
import { useEffect, useMemo, useState } from "react";
import { useConfig } from "render/context/ConfigContext";
import { useLegalDocumentQuery } from "render/hooks/api/queries/useLegalDocumentQuery";
import { useOnboardingQuery } from "render/hooks/api/queries/useOnboardingQuery";
import { usePatientConsentsQuery } from "render/hooks/api/queries/usePatientConsentsQuery";
import { paths, queries } from "render/routes/paths";
import { FullScreenPageLayout } from "render/ui/layout/FullScreenPageLayout";
import { useConsentsMap } from "render/views/ProfileView/components/DataAndPrivacySection/hooks/useConsentsMap";
import { AgreeToAutomaticSummariesView } from "./components/RequiresAutomaticSummaryState";
import { RequiresOnboardingState } from "./components/RequiresOnboardingState/RequiresOnboardingState";
import { SuccessState } from "./components/SuccessState";

interface CheckInSuccessProps {
  slot: BookingSlot;
  patient: Patient;
  roomId: string;
}

const State = {
  AutoSummariesConsent: "auto-summaries-consent",
  Onboarding: "onboarding",
  Done: "done",
};
const Step = {
  Unknown: -1,
  AutoSummariesConsent: 0,
  Onboarding: 1,
  Done: 2,
};

export function CheckInSuccess({ slot, patient, roomId }: CheckInSuccessProps) {
  const [isDone, setIsDone] = useState(false);
  const { appConfig } = useConfig();
  const [params, setParams] = useQueryParams(queries.checkIn);
  const nav = useNav(paths.checkIn, queries.checkIn);

  const legalDocumentQuery = useLegalDocumentQuery(
    generateDocumentUrl(
      appConfig.legalApiUrl,
      LegalDocument.AutomaticSummary,
      patient.preferredLanguage ?? "sv"
    )
  );
  const onboardingQuery = useOnboardingQuery();
  const patientConsentsQuery = usePatientConsentsQuery();
  const { studies } = useConsentsMap();
  const hasAgreedToAutomaticSummaries = useMemo(() => {
    if (!patientConsentsQuery.data) {
      return false;
    }

    return hasAgreedTo(
      LegalDocument.AutomaticSummary,
      patientConsentsQuery.data
    );
  }, [patientConsentsQuery.data]);
  const isProfileComplete = useMemo(() => {
    if (!patientConsentsQuery.data) {
      return false;
    }
    return isOnboardingComplete({
      requiredStudies: studies,
      onboardingData: onboardingQuery.data,
      patientConsents: patientConsentsQuery.data,
    });
  }, [patientConsentsQuery.data, onboardingQuery, studies]);

  const currentStep = useMemo(() => {
    const state = params.state[0];
    if (isDone) {
      return Step.Done;
    }
    if (state == null) {
      return Step.Unknown;
    }
    if (state === State.AutoSummariesConsent) {
      return Step.AutoSummariesConsent;
    }
    if (state === State.Onboarding) {
      return Step.Onboarding;
    }
    return Step.Done;
  }, [params.state, isDone]);

  useEffect(() => {
    if (currentStep === Step.Done) {
      setIsDone(true);
    }
  }, [currentStep]);

  useEffect(() => {
    if (isDone) {
      return;
    }
    if (params.state[0] != null) {
      return;
    }
    if (
      !onboardingQuery.isFetched ||
      !patientConsentsQuery?.data ||
      !legalDocumentQuery.data
    ) {
      return;
    }
    if (
      !hasAgreedToAutomaticSummaries &&
      params.state[0] !== State.AutoSummariesConsent
    ) {
      setParams({ state: [State.AutoSummariesConsent] });
      return;
    }
    if (!isProfileComplete && params.state[0] !== State.Onboarding) {
      setParams({ state: [State.Onboarding] });
      return;
    }
    setParams({ state: [State.Done] });
  }, [
    isDone,
    hasAgreedToAutomaticSummaries,
    isProfileComplete,
    legalDocumentQuery.data,
    onboardingQuery.isFetched,
    params.state,
    patientConsentsQuery?.data,
    setParams,
  ]);

  if (
    !onboardingQuery.isFetched ||
    !patientConsentsQuery?.data ||
    !legalDocumentQuery.data
  ) {
    return <FullScreenPageLayout />;
  }

  return (
    <FullScreenPageLayout>
      <ViewStack>
        <Slide
          active={currentStep >= Step.AutoSummariesConsent}
          direction={Direction.Right}
        >
          <AgreeToAutomaticSummariesView
            document={legalDocumentQuery.data}
            patient={patient}
            onSuccess={() => {
              if (!isProfileComplete) {
                nav.go({ roomId }, { state: [State.Onboarding] });
              } else {
                nav.go({ roomId }, { state: [State.Done] });
              }
            }}
            onSkip={() => {
              if (!isProfileComplete) {
                nav.go({ roomId }, { state: [State.Onboarding] });
              } else {
                nav.go({ roomId }, { state: [State.Done] });
              }
            }}
          />
        </Slide>
        <Slide
          active={currentStep >= Step.Onboarding}
          direction={Direction.Right}
        >
          <RequiresOnboardingState />
        </Slide>
        <Slide active={currentStep >= Step.Done} direction={Direction.Right}>
          <SuccessState slotId={slot.id} roomId={roomId} />
        </Slide>
      </ViewStack>
    </FullScreenPageLayout>
  );
}
